import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation, NavLink } from "react-router-dom";
import { TID } from "../lib/testIds";
import ThemeToggle from "./ThemeToggle";
import { useEasterEggs } from "./EasterEggs";
import Logo from "./Logo";

const LINKS = [
  { label: "Services", to: "/services", id: TID.nav.linkServices },
  { label: "Work", to: "/work", id: TID.nav.linkWork },
  { label: "Process", to: "/process", id: TID.nav.linkProcess },
  { label: "About", to: "/about", id: "nav-link-about" },
  { label: "Blog", to: "/blog", id: "nav-link-blog" },
  { label: "Components", to: "/components", id: "nav-link-components" },
  { label: "Contact", to: "/contact", id: TID.nav.linkContact },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const { logoClick } = useEasterEggs();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen
          ? "bg-canvas/70 backdrop-blur-2xl border-b border-hairline"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={TID.nav.logo}
          aria-label="Ulties — home"
          onClick={logoClick}
          className="group inline-flex items-center"
        >
          <Logo
            variant="wordmark"
            className="h-7 md:h-8 w-auto text-primary-x transition-transform group-hover:scale-[1.02] origin-left"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `px-4 py-2 text-sm transition-colors ${
                  isActive ? "text-primary-x" : "text-secondary-x hover:text-primary-x"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Link
            to="/contact"
            data-testid={TID.nav.cta}
            className="hidden sm:inline-flex group relative items-center gap-2 rounded-full px-4 md:px-5 py-2 md:py-2.5 text-sm font-medium transition-opacity hover:opacity-90"
            style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
          >
            Start a project
            <span className="transition-transform group-hover:translate-x-0.5">→</span>
          </Link>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border border-hairline-strong bg-glass text-primary-x"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {mobileOpen ? (
                <>
                  <path d="M6 6l12 12" />
                  <path d="M18 6l-12 12" />
                </>
              ) : (
                <>
                  <path d="M4 7h16" />
                  <path d="M4 17h16" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="md:hidden border-t border-hairline bg-canvas/95 backdrop-blur-2xl"
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.label}
                to={l.to}
                data-testid={`${l.id}-mobile`}
                className={({ isActive }) =>
                  `font-display text-3xl py-2 ${
                    isActive ? "text-primary-x" : "text-secondary-x"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid={`${TID.nav.cta}-mobile`}
              className="mt-4 inline-flex items-center justify-between rounded-full px-5 py-3 text-sm font-medium"
              style={{ background: "var(--text-primary)", color: "var(--canvas)" }}
            >
              Start a project
              <span>→</span>
            </Link>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
